import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import AppraisalComponentsTab from '@/components/AppraisalComponentsTab';

// Builders reuse the Components form's Flashing (+ Dektite) sections — one list, counted once.
// flashingOnly: the builder prices its own Dektites (concrete), so only Flashing pins come in.
export default function ComponentsFlashingEmbed({ api, appraisalId, pinCount = 0, flashingOnly = false, testid = 'repairs-components-flashing' }) {
  const [key, setKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const sections = flashingOnly ? ['Flashing'] : ['Flashing', 'Aquaseal'];
  useEffect(() => {
    const bump = () => setKey(k => k + 1);
    window.addEventListener('rtr:components-refresh', bump);
    return () => window.removeEventListener('rtr:components-refresh', bump);
  }, []);
  const pullPins = async (silent = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/appraisals/${appraisalId}/roof-diagram-annotations/push-to-components?only=${flashingOnly ? 'flashing_pins' : 'flashing'}`);
      if (!silent || data?.lines_created) {
        toast.success(data?.lines_created
          ? `${data.lines_created} ${flashingOnly ? 'flashing' : 'flashing/Dektite'} line(s) brought in from the diagram`
          : `No ${flashingOnly ? 'flashing' : 'flashing or penetration'} pins on the diagram`);
      }
      setKey(k => k + 1);
      window.dispatchEvent(new Event('rtr:child-quote-refresh'));
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || 'Could not bring in diagram pins');
    } finally { setBusy(false); }
  };
  // Automatic: when this quote has no lines in these sections yet, bring the diagram pins in once on open.
  useEffect(() => {
    let alive = true;
    api.get(`/appraisals/${appraisalId}/component-lines`).then(({ data }) => {
      const g = data?.groups || {};
      if (alive && !sections.some(s => (g[s] || []).length)) pullPins(true);
    }).catch(() => {});
    return () => { alive = false; };
  }, [api, appraisalId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid={testid} className="space-y-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">{flashingOnly ? 'Flashings' : <>Flashings &amp; Dektites</>}</div>
        <button type="button" data-testid={testid === 'repairs-components-flashing' ? 'repairs-flashing-pull-pins' : `${testid}-pull-pins`} onClick={() => pullPins(false)} disabled={busy}
          className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded border border-[#D35400] text-[#D35400] hover:bg-[#FFF7ED] disabled:opacity-50 transition-colors">
          <Download size={12} /> {busy ? 'Bringing in…' : `Refresh diagram pins${pinCount ? ` (${pinCount})` : ''}`}
        </button>
      </div>
      <p className="text-[11px] text-slate-500 italic">
        {flashingOnly
          ? 'Diagram Flashing pins come in automatically — pick the flashing type, template and folds, or add extra flashings. Refreshing keeps your edits. Their total is added to this quote.'
          : 'Diagram Flashing / Penetration pins come in automatically — pick the flashing type, template, backtray and Dektite, or add extra flashings. Refreshing keeps your edits. Their total is added to this quote.'}
      </p>
      <AppraisalComponentsTab key={key} api={api} appraisalId={appraisalId} onlySections={sections} onJumpTab={() => {}} />
    </div>
  );
}
